import {
  AdvertiserControllerApi,
  AdvertiserReportControllerApi,
  AiResponseControllerApi,
  AiResponseFeedbackControllerApi,
  AudienceControllerApi,
  AudienceReportControllerApi,
  AutomatedNotesScheduleControllerApi,
  BudgetGroupControllerApi,
  CampaignControllerApi,
  CampaignReportControllerApi,
  CampaignReportRunnerControllerApi,
  CampaignUpdatesControllerApi,
  ClientControllerApi,
  ClientReportControllerApi,
  CognitoUserControllerApi,
  Configuration,
  CrmCsvControllerApi,
  CronControllerApi,
  CustomReportingControllerApi,
  DashboardDatasetConfigControllerApi,
  EmailControllerApi,
  FileControllerApi,
  FormControllerApi,
  InternalUserControllerApi,
  InvoiceControllerApi,
  JiraMetadataControllerApi,
  MagicBellNotificationControllerApi,
  MonitorConfigControllerApi,
  MonitorControllerApi,
  MonitorDependencyControllerApi,
  MonitorEntityViolationControllerApi,
  MonitorRunControllerApi,
  MonitorSummaryControllerApi,
  NotificationControllerApi,
  OfflineConversionsUploadControllerApi,
  PersonaControllerApi,
  PreferencesControllerApi,
  ProductControllerApi,
  PromptConfigControllerApi,
  PromptContentControllerApi,
  ProvenProcessControllerApi,
  ReportControllerApi,
  ReportVisualizationQueryControllerApi,
  StandardUserControllerApi,
  SupportControllerApi,
  UserControllerApi,
} from '@kortxio/hub-api';
import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { getCognitoJwtToken } from 'features/auth/cognitoFunctions';
import { isDemoMode } from 'features/demo-mode/helpers';
import { toDemoModeResponse } from 'features/demo-mode/mappers';
import { isAbortedError } from 'libs/api/helper';
import config from 'libs/config';
import { errorHandler } from 'libs/error';

const initAxiosInstance = (isDemoMode: boolean) => {
  const axiosInstance = axios.create({
    baseURL: config.api.apiUrl,
  });

  axiosInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      config.headers.Authorization = await getCognitoJwtToken();

      return config;
    },
    (error: AxiosError) => {
      errorHandler.handle(error);

      return Promise.reject(error);
    }
  );

  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      if (!isDemoMode) {
        return response;
      }

      return toDemoModeResponse(response);
    },
    (error: AxiosError) => {
      if (!isAbortedError(error)) {
        errorHandler.handle(error);
      }

      return Promise.reject(error);
    }
  );

  return axiosInstance;
};

export const axiosInstance = initAxiosInstance(isDemoMode());

const apiConfiguration = new Configuration({
  basePath: config.api.apiUrl,
});

const initApi = () => {
  return {
    advertiser: new AdvertiserControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    advertiserReports: new AdvertiserReportControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    aiResponse: new AiResponseControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    aiResponseFeedback: new AiResponseFeedbackControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    audiences: new AudienceControllerApi(apiConfiguration, '', axiosInstance),
    audienceReports: new AudienceReportControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    automaticNoteSchedules: new AutomatedNotesScheduleControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    budgetGroup: new BudgetGroupControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    campaign: new CampaignControllerApi(apiConfiguration, '', axiosInstance),
    campaignReports: new CampaignReportControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    campaignReportRunner: new CampaignReportRunnerControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    campaignUpdates: new CampaignUpdatesControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    cognitoUser: new CognitoUserControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    client: new ClientControllerApi(apiConfiguration, '', axiosInstance),
    clientReports: new ClientReportControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    crmCsv: new CrmCsvControllerApi(apiConfiguration, '', axiosInstance),
    cron: new CronControllerApi(apiConfiguration, '', axiosInstance),
    dashboardDatasetConfigs: new DashboardDatasetConfigControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    email: new EmailControllerApi(apiConfiguration, '', axiosInstance),
    file: new FileControllerApi(apiConfiguration, '', axiosInstance),
    forms: new FormControllerApi(apiConfiguration, '', axiosInstance),
    internalUser: new InternalUserControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    invoice: new InvoiceControllerApi(apiConfiguration, '', axiosInstance),
    jira: new JiraMetadataControllerApi(apiConfiguration, '', axiosInstance),
    magicBell: new MagicBellNotificationControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    monitorConfig: new MonitorConfigControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    monitor: new MonitorControllerApi(apiConfiguration, '', axiosInstance),
    monitorDependency: new MonitorDependencyControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    monitorEntityViolation: new MonitorEntityViolationControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    monitorRun: new MonitorRunControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    monitorSummary: new MonitorSummaryControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    notification: new NotificationControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    offlineConversionsUpload: new OfflineConversionsUploadControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    persona: new PersonaControllerApi(apiConfiguration, '', axiosInstance),
    prompt: new PromptConfigControllerApi(apiConfiguration, '', axiosInstance),
    promptContent: new PromptContentControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    product: new ProductControllerApi(apiConfiguration, '', axiosInstance),
    provenProcess: new ProvenProcessControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    report: new ReportControllerApi(apiConfiguration, '', axiosInstance),
    standardUser: new StandardUserControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    support: new SupportControllerApi(apiConfiguration, '', axiosInstance),
    user: new UserControllerApi(apiConfiguration, '', axiosInstance),
    customReporting: new CustomReportingControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    preference: new PreferencesControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
    reportVisualization: new ReportVisualizationQueryControllerApi(
      apiConfiguration,
      '',
      axiosInstance
    ),
  };
};

const api = initApi();

export default api;
